// Shared by ColorSwatch (initial page-load correction) and ColorSwatchRadios
// (color/size hover): applies a specific variant's price to a card's existing
// price markup, following the same rules as price.liquid/unified-discount-badge.liquid
// — a variant's own real markdown (clearance) always wins; otherwise, only on
// cards paired with the unified discount badge, the flat sitewide promo
// (settings.product_discount) is folded into compare/price so the existing
// price--on-sale markup shows the reduction. `elements` is the set of DOM nodes
// price.liquid/unified-discount-badge.liquid already rendered for this card:
// { priceWrapper, price, comparePrice, salePrice, discountBlock, unifiedBadgeData }.
function applyVariantPrice(container, elements, variant, isClearanceVariant) {
  if (!elements.priceWrapper || !elements.price) return;

  const usesUnifiedBadge = !!elements.unifiedBadgeData;

  let effectiveCompare = variant.compare_at_price;
  let effectivePrice = variant.price;
  // True only when the markdown shown comes from the code-based promo (not the
  // variant's own compare_at_price), so the discount code is shown only then.
   let promoApplied = false;
  if (usesUnifiedBadge && !(effectiveCompare > effectivePrice) && !isClearanceVariant) {
    const tagPromo = elements.unifiedBadgeData.dataset.tagPromo === 'true';
    const settingDiscount = parseFloat(elements.unifiedBadgeData.dataset.settingDiscount) || 0;
    if (tagPromo && settingDiscount > 0) {
      const discountAmount = Math.floor(effectivePrice * settingDiscount / 100);
      effectiveCompare = effectivePrice;
      effectivePrice = effectivePrice - discountAmount;
      promoApplied = true;
    }
  }

  elements.priceWrapper.classList.remove('price--sold-out');
  elements.priceWrapper.classList.remove('price--on-sale');
  elements.priceWrapper.classList.remove('price--no-compare');

  if (variant.available == false) {
    elements.priceWrapper.classList.add('price--sold-out');
  }
  else if (effectiveCompare > effectivePrice) {
    elements.priceWrapper.classList.add('price--on-sale');
  }

  // Mirrors price.liquid's own formatted_money_price branching: its "no From"
  // case only exists for the uncorrected, price_min-based render (target ==
  // product). The instant a specific variant's own price/compare_at is being
  // shown — which is exactly what this correction does — price.liquid's own
  // "else" branch always includes "From ", regardless of whether that variant
  // itself happens to be on a real markdown.
  const priceVaries = elements.priceWrapper.dataset.priceVaries === 'true';
  const fromPrefixHtml = priceVaries ? '<span class="start-fr">From </span>' : '';
  elements.price.innerHTML = '<price-money class="custom-main-price">' + fromPrefixHtml + '<bdi>' + theme.Currency.formatMoney(effectivePrice, theme.shopSettings.moneyFormat) + '</bdi></price-money>';

  if (effectiveCompare > effectivePrice && elements.comparePrice && elements.salePrice) {
    elements.comparePrice.innerHTML = '<price-money class="custom-main-price">' + fromPrefixHtml + '<bdi>' + theme.Currency.formatMoney(effectiveCompare, theme.shopSettings.moneyFormat) + '</bdi></price-money>';
    elements.salePrice.innerHTML = '<price-money><bdi>' + theme.Currency.formatMoney(effectivePrice, theme.shopSettings.moneyFormat) + '</bdi></price-money>';
  }

  if (usesUnifiedBadge) {
    let percent = 0;
    if (effectiveCompare > effectivePrice && effectivePrice > 0) {
      percent = Math.round((effectiveCompare - effectivePrice) / effectiveCompare * 100);
    }
    let badge = container.querySelector('.unified-discount-badge');
    if (percent > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'unified-discount-badge test';
        elements.unifiedBadgeData.insertAdjacentElement('afterend', badge);
      }
      // badge.textContent = percent + '% off';
      badge.textContent = percent + '% off ';
      // Re-append the promo code (e.g. "| PREVIEW15") that textContent just wiped out.
      // data-discount-code is already filtered by unified-discount-badge.liquid's
      // title exclusions, so an empty value means this product shouldn't show one.
      const discountCode = elements.unifiedBadgeData.dataset.discountCode;
      if (discountCode && promoApplied) {
        const codeEl = document.createElement('span');
        codeEl.className = 'cus-discount-code';
        codeEl.textContent = '| ' + discountCode;
        badge.appendChild(codeEl);
      }
    } else if (badge) {
      badge.remove();
    }
  }

  // Older inline discount block(s) (snippets/price.liquid's .sale_price_block).
  // Queries fresh, scoped to this card's container, and updates every match —
  // not just elements.discountBlock's single cached reference — so this can't
  // silently miss a second copy of the block within the same card.
  const discountBlocks = container ? container.querySelectorAll('.sale_price_block') : [];
  discountBlocks.forEach((discountBlock) => {
    const discountPercent = parseFloat(discountBlock.dataset.discountPercent) || 0;
    // basePrice = the active variant's own price (variant.price, in cents); rounded
    // to the nearest cent rather than floored, matching Math.round(basePrice * (1 -
    // discountPercent / 100)) but kept in cents so it stays consistent with
    // price.liquid's own (also cent-based) server-rendered calculation.
    const discountedPrice = Math.round(variant.price * (1 - discountPercent / 100));
    const mainMoneyEl = discountBlock.querySelector('.main_money');
    const decimalEl = discountBlock.querySelector('.price__suffix');
    if (mainMoneyEl) mainMoneyEl.textContent = Math.floor(discountedPrice / 100);
    if (decimalEl) decimalEl.textContent = '.' + String(discountedPrice % 100).padStart(2, '0');
  });
}

// Collection-grid cards otherwise always show whichever swatch is first in
// the product's own option order, ignoring any color filter the shopper has
// applied in the facet sidebar (e.g. filtering to "Blue" still shows a card's
// default "White" swatch/image/price). This matches each card's swatches
// against the active filter(s) and selects the best match instead.
//
// Filter checkboxes here are grouped (one checkbox's value is an opaque
// FilterSettingGroup id that can bundle several real option values under one
// label, e.g. "White / Neutral" or "Blue / Navy") — Shopify doesn't expose
// which real option values a group like "Grey" actually bundles (e.g.
// "Silver", "Charcoal", "Slate"), only the display label — so there's no
// direct id to match against a swatch's own option value. Best effort:
// split the active filter's visible label into tokens and match those
// against each swatch's title — exact match, then substring, then a curated
// color-family synonym list for the common case where the group label and
// the swatch's real name are synonyms with no shared substring at all
// (e.g. filter label "Grey" vs. swatch named "Silver").
var COLOR_FAMILY_SYNONYMS = {
  white: ['white', 'ivory', 'snow', 'linen', 'cream'],
  neutral: ['neutral', 'oatmeal', 'sand', 'stone', 'taupe', 'greige', 'ecru', 'wheat', 'natural'],
  grey: ['grey', 'gray', 'silver', 'charcoal', 'slate', 'ash', 'dove', 'pewter', 'smoke', 'fog'],
  black: ['black', 'noir', 'onyx', 'coal', 'jet'],
  blue: ['blue', 'navy', 'cobalt', 'denim', 'indigo', 'sky', 'azure', 'cerulean', 'cornflower', 'moonlight', 'cloud', 'mist'],
  teal: ['teal', 'lagoon', 'seaglass', 'aqua', 'turquoise'],
  green: ['green', 'sage', 'olive', 'moss', 'eucalyptus', 'emerald', 'forest', 'mint', 'basil'],
  beige: ['beige', 'tan', 'khaki', 'camel', 'nude', 'mushroom'],
  pink: ['pink', 'blush', 'rose', 'mauve', 'opal', 'quartz'],
  red: ['red', 'brick', 'rust', 'burgundy', 'wine', 'maroon', 'port', 'spice'],
  yellow: ['yellow', 'mustard', 'gold', 'ochre', 'sorbet'],
  purple: ['purple', 'lavender', 'lilac', 'plum', 'thistle'],
  brown: ['brown', 'mocha', 'chocolate', 'espresso', 'cocoa', 'walnut', 'toasted nut'],
};

function colorFamiliesOf(text) {
  return Object.keys(COLOR_FAMILY_SYNONYMS).filter((family) =>
    COLOR_FAMILY_SYNONYMS[family].some((word) => text.includes(word))
  );
}

function syncSwatchesWithActiveColorFilters(scope) {
  const root = scope || document;
  const checkedColorFilters = document.querySelectorAll('input[name^="filter.v.option.color"]:checked');
  if (!checkedColorFilters.length) return;

  const tokens = new Set();
  checkedColorFilters.forEach((input) => {
    const label = document.querySelector(`label[for="${input.id}"]`);
    const text = (label && (label.getAttribute('title') || label.textContent)) || '';
    text.split(/[/,&]| and /i).forEach((part) => {
      const token = part.trim().toLowerCase();
      if (token) tokens.add(token);
    });
  });
  if (!tokens.size) return;

  const tokenFamilies = new Set();
  tokens.forEach((token) => colorFamiliesOf(token).forEach((family) => tokenFamilies.add(family)));

  root.querySelectorAll('.card-wrapper').forEach((card) => {
    const swatches = Array.from(card.querySelectorAll('color-swatch'));
    if (!swatches.length) return;

    const swatchValue = (swatch) => (swatch.getAttribute('title') || '').trim().toLowerCase();
    const alreadyActive = swatches.find((swatch) => swatch.classList.contains('is-active'));

    let match = swatches.find((swatch) => tokens.has(swatchValue(swatch)));
    if (!match) {
      match = swatches.find((swatch) => {
        const value = swatchValue(swatch);
        return value && Array.from(tokens).some((token) => value.includes(token) || token.includes(value));
      });
    }
    if (!match && tokenFamilies.size) {
      match = swatches.find((swatch) => {
        const value = swatchValue(swatch);
        return value && colorFamiliesOf(value).some((family) => tokenFamilies.has(family));
      });
    }

    if (match && match !== alreadyActive && typeof match.onHoverHandler === 'function') {
      match.onHoverHandler();
    }
  });
}

document.addEventListener('DOMContentLoaded', () => syncSwatchesWithActiveColorFilters());

class ColorSwatch extends HTMLElement {
  constructor() {
    super();

    this.cached = {};
    this.variantId = this.dataset.variantId;
    this.container = this.closest('.card-wrapper');

    if (this.variantId || theme.config.mqlSmall) {
      this.addEventListener('mouseenter', this.onHoverHandler.bind(this));
      this.querySelector('a').addEventListener('click', (event) => event.preventDefault());
    }
    else {
      this.addEventListener('mouseenter', this.onHoverHandler.bind(this));
    }

    // On initial page load, price.liquid renders this card's price from
    // product.price/price_min (the cheapest variant site-wide), not necessarily
    // the variant this card actually displays (its first swatch/image). Correct
    // it once, using that exact variant's own data (see card-product.liquid).
    if (this.classList.contains('is-active') && this.dataset.activePrice) {
      this.correctInitialPrice();
    }

    // Same gap for the "Final Sale" badge: card-product.liquid renders it from
    // card_product.selected_or_first_available_variant, which isn't necessarily
    // this card's default-displayed swatch/variant either. updateClearanceBadge()
    // otherwise only runs on hover (onHoverHandler), so a wrong initial badge
    // never self-corrects until the shopper interacts with a swatch.
    if (this.classList.contains('is-active')) {
      this.updateClearanceBadge();
    }
  }

  correctInitialPrice() {
    // `this` (`activeSwatch`) is the one color-swatch element on THIS card carrying
    // is-active + data-active-price — see card-product.liquid. Its own price/compare_at/
    // clearance flag are the only source used below; nothing here reads product.price,
    // product.price_min, or product.selected_or_first_available_variant.
    const activeSwatch = this;
    const basePrice = Number(activeSwatch.dataset.activePrice);
    if (!basePrice || !this.container) return;

    const compareAtPrice = Number(activeSwatch.dataset.activeCompareAtPrice) || 0;
    const isClearanceVariant = activeSwatch.dataset.activeClearance === 'true';

    const elements = {
      priceWrapper: this.container.querySelector('.price'),
      price: this.container.querySelector('.price__regular .price-item--regular'),
      comparePrice: this.container.querySelector('.price__sale .price-item--regular'),
      salePrice: this.container.querySelector('.price__sale .price-item--sale'),
      discountBlock: this.container.querySelector('.sale_price_block'),
      unifiedBadgeData: this.container.querySelector('.unified-discount-badge-data'),
    };

    applyVariantPrice(this.container, elements, { price: basePrice, compare_at_price: compareAtPrice, available: true }, isClearanceVariant);
  }

  onHoverHandler() {
    this.updateClearanceBadge();

    if (this.variantId) {
      (theme.config.mqlSmall || theme.config.isTouch) ? this.colorSwatchChange() : this.colorSwatchFetch();
    }
    else {
      this.colorSwatchChange();
    }
  }

  // Toggles the top-left card badge to "Final Sale" based on this swatch's
  // own data-clearance attribute. Runs independently of colorSwatchFetch so
  // it works even when updatePrice() bails out (see its color-swatch-radios
  // guard) — it only swaps the badge slot, never the price itself, since a
  // color-only hover can't know which size is selected.
  updateClearanceBadge() {
    const slot = this.container.querySelector('.card-badge__sale-slot');
    if (!slot) return;

    if (this.dataset.clearance === 'true') {
      if (slot.dataset.originalHtml === undefined) slot.dataset.originalHtml = slot.innerHTML;
      slot.innerHTML = '<span class="badge badge--onsale clearance-badge" aria-hidden="true">Final Sale</span>';
    }
    else if (slot.dataset.originalHtml !== undefined) {
      slot.innerHTML = slot.dataset.originalHtml;
    }
  }

  colorSwatchChange(forceUpdate) {
    const image = this.container.querySelector('.card--image-animate.media img');
    const pictureSource = this.container.querySelector('.card--image-animate.media source');

    if (image !== null || pictureSource != null || forceUpdate) {
      if (image !== null) {
        image.src = this.dataset.src;
        image.srcset = this.dataset.srcset;
      }

      if (pictureSource !== null) {
        pictureSource.srcset = this.dataset.srcset;
      }

      const swatches = this.container.querySelectorAll('.color-swatch');
      swatches.forEach((swatch) => {
        swatch.classList.remove('is-active');
      });
  
      this.classList.add('is-active');

        // Remembered so Quick View (quick-view.js) opens on this same color.
      // data-variant-id is only rendered for quick-buy cards, so fall back to
      // the swatch link's ?variant= param.
      const link = this.querySelector('a');
      const selectedVariantId = this.variantId || (link && new URL(link.href, window.location.origin).searchParams.get('variant'));
      if (selectedVariantId) this.container.dataset.selectedVariantId = selectedVariantId;

      // Revert div to a href for wrapper
      this.container.querySelector('.card--image-animate.media').style.display = "block";
      if(this.container.querySelector('.card-media-video')) this.container.querySelector('.card-media-video').style.display = "none";
    }
  }

  colorSwatchFetch() {
    const productHandle = this.dataset.productHandle;
    const collectionHandle = this.dataset.collectionHandle;
    let sectionUrl = `${theme.routes.root_url}/products/${productHandle}?variant=${this.variantId}&view=card`;

    if (collectionHandle.length > 0) {
      sectionUrl = `${theme.routes.root_url}/collections/${collectionHandle}/products/${productHandle}?variant=${this.variantId}&view=card`;
    }

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace('//', '/');

    if (this.cached[sectionUrl]) {
      this.renderProductInfo(this.cached[sectionUrl]);
      return;
    }

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.cached[sectionUrl] = html;
        this.renderProductInfo(html);
      })
      .catch(e => {
        console.error(e);
      });
  }

  renderProductInfo(html) {
      // updateButtons(html) intentionally not called: it swapped the card's
    // "Choose options" button for templates/product.card.liquid's inline
    // size-selection bar on swatch hover. The card keeps its own button.
    
    // this.updateButtons(html);
    this.updateTitle(html);
    this.updatePrice(html);

    this.colorSwatchChange(true);
  }

  updateTitle(html) {
    const selector = '.card-information__text';
    const destination = this.container.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) destination.innerHTML = source.innerHTML + '<small>&ndash;&nbsp;' + this.getAttribute('title') + '</small>';
  }

  updatePrice(html) {
    if (html.querySelector('color-swatch-radios')) return;

    const selector = '.price';
    const destination = this.container.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.setAttribute('class', source.getAttribute('class'));
    }
  }

  updateButtons(html) {
    const selector = '.card-information__button';
    const destination = this.container.querySelector(selector);
    const source = html.querySelector(selector);

    if (source && destination) destination.innerHTML = source.innerHTML;

    destination.classList.remove('is-expanded');
    if (source.classList.contains('is-expanded')) destination.classList.add('is-expanded');
  }
}
customElements.define('color-swatch', ColorSwatch);

class ColorSwatchRadios extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      soldOut: 'price--sold-out',
      onSale: 'price--on-sale',
      noCompare: 'price--no-compare',
      hidden: 'hidden',
      loading: 'loading'
    };
    this.selectors = {
      cardWrapper: '.card-wrapper',
      priceWrapper: '.price',
      price: '.price__regular .price-item--regular',
      comparePrice: '.price__sale .price-item--regular',
      salePrice: '.price__sale .price-item--sale',
      discountBlock: '.sale_price_block',
      unifiedBadgeData: '.unified-discount-badge-data',
      unifiedBadge: '.unified-discount-badge',
      unitWrapper: '.unit-price',
      unitPrice: '[data-unit-price]',
      unitPriceBaseUnit: '[data-unit-base]'
    };

    try {
      this.clearanceMap = JSON.parse(this.dataset.clearanceMap || '{}');
    } catch (e) {
      this.clearanceMap = {};
    }

    this.miniCart = document.querySelector('mini-cart');

    this.init();
    this.addEventListener('change', this.onVariantChange);

    const buttons = this.querySelectorAll('.swatch:not(.disabled)');
    buttons.forEach((button) => button.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    buttons.forEach((button) => button.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));

    const inputs = this.querySelectorAll('.swatch:not(.disabled) input');
    inputs.forEach((input) => input.addEventListener('click', this.onClickHandler.bind(this)));
  }

  cacheElements() {
    this.container = this.closest(this.selectors.cardWrapper);
    this.cache = {
      priceWrapper: this.container.querySelector(this.selectors.priceWrapper),
      price: this.container.querySelector(this.selectors.price),
      comparePrice: this.container.querySelector(this.selectors.comparePrice),
      salePrice: this.container.querySelector(this.selectors.salePrice),
      discountBlock: this.container.querySelector(this.selectors.discountBlock),
      unifiedBadgeData: this.container.querySelector(this.selectors.unifiedBadgeData),
      priceHTML: this.container.querySelector(this.selectors.priceWrapper).cloneNode(true).innerHTML,
      priceClass: this.container.querySelector(this.selectors.priceWrapper).cloneNode(true).getAttribute('class'),
    };
  }

  onClickHandler(event) {
    const input = event.currentTarget;
    const button = input.parentNode;
    const variantId = this.currentVariant.id;

    if (variantId) {
      if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
        Shopify.postLink(theme.routes.cart_add_url, {
          parameters: {
            id: variantId,
            quantity: 1
          },
        });
        return;
      }

      input.setAttribute('disabled', '');
      button.classList.add(this.classes.loading);
      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          this.miniCart && this.miniCart.renderContents(parsedState);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          input.removeAttribute('disabled');
          button.classList.remove(this.classes.loading);
        });
    }
  }

  onEnterHandler(event) {
    const target = event.currentTarget;
    const input = target.querySelector('input');
    input.checked = true;
    this.dispatchEvent(new Event('change'));

    setTimeout(() => {
      this.cacheElements();
      this.updatePrice();
      this.updateUnitPrice();
    });
  }

  onLeaveHandler() {
    this.cache.priceWrapper.innerHTML = this.cache.priceHTML;
    this.cache.priceWrapper.setAttribute('class', this.cache.priceClass);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
  }

  init() {
    this.updateOptions();
    this.updateMasterId();
    this.setAvailability();
  }

  updatePrice() {
    const variant = this.currentVariant;
    if (!variant) return;

    const isClearanceVariant = !!this.clearanceMap[variant.id];
    applyVariantPrice(this.container, this.cache, variant, isClearanceVariant);
  }

  updateUnitPrice() {
    const variant = this.currentVariant;
  
    if (this.container.querySelector(this.selectors.unitWrapper)) {
      if (variant && variant.unit_price) {
        this.container.querySelector(this.selectors.unitPrice).innerHTML = '<price-money><bdi>' + theme.Currency.formatMoney(variant.unit_price, theme.shopSettings.moneyFormat) + '</bdi></price-money>';
        this.container.querySelector(this.selectors.unitPriceBaseUnit).innerHTML = theme.Currency.getBaseUnit(variant);
        this.container.querySelector(this.selectors.unitWrapper).classList.remove(this.classes.hidden);
      }
      else {
        this.container.querySelector(this.selectors.unitWrapper).classList.add(this.classes.hidden);
      }
    }
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData()?.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  setAvailability() {
    if (!this.currentVariant) return;

    this.querySelectorAll('.variant-input-wrapper').forEach(group => {
      this.disableVariantGroup(group);
    });

    const currentlySelectedValues = this.currentVariant.options.map((value,index) => {return {value, index: `option${index+1}`}})
    const initialOptions = this.createAvailableOptionsTree(this.variantData, currentlySelectedValues, this.currentVariant);

    for (var [option, values] of Object.entries(initialOptions)) {
      this.manageOptionState(option, values);
    }
  }

  enableVariantOption(group, obj) {
    const value = obj.value.replace(/([ #;&,.+*~\':"!^$[\]()=>|\/@])/g,'\\$1');
    const input = group.querySelector('input[data-option-value="'+ value +'"]');

    if (!input) return;

    // Variant exists - enable & show variant
    input.removeAttribute('disabled');
    input.parentNode.classList.remove('disabled');

    // Variant sold out - cross out option (remains selectable)
    if (obj.soldOut) {
      input.setAttribute('disabled', '');
      input.parentNode.classList.add('disabled');
    }
  }

  disableVariantGroup(group) {
    group.querySelectorAll('input').forEach((input) => {
      input.setAttribute('disabled', '');
      input.parentNode.classList.add('disabled');
    });
  }

  manageOptionState(option, values) {
    const group = this.querySelector('.variant-input-wrapper[data-option-index="'+ option +'"]');

    // Loop through each option value
    values.forEach(obj => {
      this.enableVariantOption(group, obj);
    });
  }

  createAvailableOptionsTree(variants, currentlySelectedValues) {
    // Reduce variant array into option availability tree
    return variants.reduce((options, variant) => {

      // Check each option group (e.g. option1, option2, option3) of the variant
      Object.keys(options).forEach(index => {

        if (variant[index] === null) return;

        let entry = options[index].find(option => option.value === variant[index]);

        if (typeof entry === 'undefined') {
          // If option has yet to be added to the options tree, add it
          entry = {value: variant[index], soldOut: true}
          options[index].push(entry);
        }

        const currentOption1 = currentlySelectedValues.find(({value, index}) => index === 'option1')
        const currentOption2 = currentlySelectedValues.find(({value, index}) => index === 'option2')

        switch (index) {
          case 'option1':
            // Option1 inputs should always remain enabled based on all available variants
            entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            break;
          case 'option2':
            // Option2 inputs should remain enabled based on available variants that match first option group
            if (currentOption1 && variant['option1'] === currentOption1.value) {
              entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            }
          case 'option3':
            // Option 3 inputs should remain enabled based on available variants that match first and second option group
            if (
              currentOption1 && variant['option1'] === currentOption1.value
              && currentOption2 && variant['option2'] === currentOption2.value
            ) {
              entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            }
        }
      })

      return options;
    }, { option1: [], option2: [], option3: []})
  }
}
customElements.define('color-swatch-radios', ColorSwatchRadios);
